import React, { useMemo, useState } from "react";
import queries from "../helpers/queries";
import { withUser } from "../Contexts/user";
import Table from "../Components/darkBasicTable";
import { useLazyQuery } from "@apollo/react-hooks";
import moment from "moment";
import excel from "../Components/excel";

const capitalizeWords = str => {
    return str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
};

const DownlineReport = () => {

    const [filters, setFilters] = useState({
        user: null,
        dateTo: moment(Date.now()).format("YYYY-MM-DD"),
        dateFrom: moment(Date.now()).subtract(1, "weeks").format("YYYY-MM-DD"),
    });
    const [report, setReport] = useState("Personal Report");
    const [totalDownlines, setTotalDownlines] = useState(0);
    const [totalSalesUnit, setTotalSalesUnit] = useState(0);
    const [totalGroupSalesUnit, setTotalGroupSalesUnit] = useState(0);
    const [totalConvertUnit, setTotalConvertUnit] = useState(0);
    const [totalGroupConvertUnit, setTotalGroupConvertUnit] = useState(0);
    const [totalGroupNetPrice, setTotalGroupNetPrice] = useState(0);
    const [totalGroupSPAPrice, setTotalGroupSPAPrice] = useState(0);
    const [totalNetPrice, setTotalNetPrice] = useState(0);
    const [totalSPAPrice, setTotalSPAPrice] = useState(0);

    const [paginationState, setPaginationState] = useState({
        page: 1,
    });

    const columns = useMemo(
        () => {
            const NumberFormatter = new Intl.NumberFormat("en-GB", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
    
            return [
                {
                    Header: "Downline",
                    accessor: "user",
                    Cell: ({ value }) => (
                        <div>{capitalizeWords(value)}</div>
                    ),
                },
                {
                    Header: "Sales Unit",
                    accessor: "personalBookingSales",
                },
                {
                    Header: "Sales Net GDV",
                    accessor: "personalNetGdv",
                    Cell: ({ value }) => (
                        <div>RM {NumberFormatter.format(value)}</div>
                    ),
                },
                {
                    Header: "Convert Units",
                    accessor: "convertUnit",
                },
                {
                    Header: "Convert Net GDV",
                    accessor: "convertNetGdv",
                    Cell: ({ value }) => (
                        <div>RM {NumberFormatter.format(value)}</div>
                    ),
                },
                {
                    Header: "Convert Percentage",
                    accessor: "convertPercentage",
                    Cell: ({ value }) => (
                        <div>{NumberFormatter.format(value)} %</div>
                    ),
                },
                {
                    Header: "Net GDV Percentage",
                    accessor: "netGdvPercentage",
                    Cell: ({ value }) => (
                        <div>{NumberFormatter.format(value)} %</div>
                    ),
                },
                {
                    Header: "Total Net Price",
                    accessor: "totalNetPrice",
                    Cell: ({ value }) => (
                        <div>RM {NumberFormatter.format(value)}</div>
                    ),
                },
                {
                    Header: "Total SPA Price",
                    accessor: "totalSPAPrice",
                    Cell: ({ value }) => (
                        <div>RM {NumberFormatter.format(value)}</div>
                    ),
                },
            ];
        },
        []
    );

    const groupColumns = useMemo(
        () => {
            const NumberFormatter = new Intl.NumberFormat("en-GB", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
            return [
                {
                    Header: "Downline",
                    accessor: "user",
                    Cell: ({ value }) => (
                        <div>{capitalizeWords(value)}</div>
                    ),
                },
                {
                    Header: "Group Sales Unit",
                    accessor: "groupBookingSales",
                },
                {
                    Header: "Group Sales Net GDV",
                    accessor: "groupPersonalNetGdv",
                    Cell: ({ value }) => (
                        <div>RM {NumberFormatter.format(value)}</div>
                    ),
                },
                {
                    Header: "Group Convert Units",
                    accessor: "groupConvertUnit",
                },
                {
                    Header: "Group Convert Net GDV",
                    accessor: "groupConvertNetGdv",
                    Cell: ({ value }) => (
                        <div>RM {NumberFormatter.format(value)}</div>
                    ),
                },
                {
                    Header: "Group Convert Percentage",
                    accessor: "groupConvertPercentage",
                    Cell: ({ value }) => (
                        <div>{NumberFormatter.format(value)} %</div>
                    ),
                },
                {
                    Header: "Group Net GDV Percentage",
                    accessor: "groupNetGdvPercentage",
                    Cell: ({ value }) => (
                        <div>{NumberFormatter.format(value)} %</div>
                    ),
                },
                {
                    Header: "Group Net Price",
                    accessor: "groupNetPrice",
                    Cell: ({ value }) => (
                        <div>RM {NumberFormatter.format(value)}</div>
                    ),
                },
                {
                    Header: "Group SPA Price",
                    accessor: "groupSPAPrice",
                    Cell: ({ value }) => (
                        <div>RM {NumberFormatter.format(value)}</div>
                    ),
                },
            ]
        },
        []
    );

    const [
        fetchDownlineSalesReportAgent,
        { loading: downlineSalesReportLoading, data: downlineSalesReportData },
    ] = useLazyQuery(queries.FETCH_MANUAL_DOWNLINE_SALES_REPORT_AGENT, {
        fetchPolicy: "network-only",
        variables: {
            by: paginationState.by ? paginationState.by : undefined,
            dir: paginationState.dir ? paginationState.dir : undefined,
            dateFrom: filters.dateFrom,
            dateTo: filters.dateTo,
        },
        onCompleted: (data) => {
            
            if (data.fetchManualDownlineSalesReportAgent) {
                const downlineData = data.fetchManualDownlineSalesReportAgent.data;
                
                // Set total downlines
                setTotalDownlines(downlineData.length);
                
                // Calculate totals
                let totalSalesUnit = 0;
                let totalConvertUnit = 0;
                let totalGroupSalesUnit = 0;
                let totalGroupConvertUnit = 0;
                let totalGroupNetPrice = 0;
                let totalGroupSPAPrice = 0;
                let totalNetPrice = 0;
                let totalSPAPrice = 0;

                
                downlineData.forEach((item) => {
                    totalSalesUnit += item.personalBookingSales;
                    totalConvertUnit += item.convertUnit;
                    totalGroupSalesUnit += item.groupBookingSales;
                    totalGroupConvertUnit += item.groupConvertUnit;
                    totalGroupNetPrice += item.groupNetPrice;
                    totalGroupSPAPrice += item.groupSPAPrice;
                    totalNetPrice += item.totalNetPrice;
                    totalSPAPrice += item.totalSPAPrice;

                });
        
                // Update state with calculated totals
                setTotalSalesUnit(totalSalesUnit);
                setTotalConvertUnit(totalConvertUnit);
                setTotalGroupSalesUnit(totalGroupSalesUnit);
                setTotalGroupConvertUnit(totalGroupConvertUnit);
                setTotalGroupNetPrice(totalGroupNetPrice);
                setTotalGroupSPAPrice(totalGroupSPAPrice);
                setTotalNetPrice(totalNetPrice);
                setTotalSPAPrice(totalSPAPrice);
            }
        }
    });

    const NumberFormatter = new Intl.NumberFormat("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    return (
        <div className="dashboard-container">
                  <h1 className="downline-title">MANUAL DOWNLINE REPORTS</h1>
            
            <div className="stats-card-container">
                <div className="stats-card">
                    <div className="stats-card-title">Total Recruits</div>
                    <div className="stats-card-value">{(totalDownlines)}</div>
                </div>
                
                <div className="stats-card">
                    <div className="stats-card-title">Total Sales Units</div>
                    <div className="stats-card-value">{(totalSalesUnit)}</div>
                </div>
                
                <div className="stats-card">
                    <div className="stats-card-title">Total Convert Units</div>
                    <div className="stats-card-value">{(totalConvertUnit)}</div>
                </div>
                
             
                
              

                <div className="stats-card">
                    <div className="stats-card-title">Total Group Sales Units</div>
                    <div className="stats-card-value">{(totalGroupSalesUnit)}</div>
                </div>
                
                <div className="stats-card">
                    <div className="stats-card-title">Total Group Convert Units</div>
                    <div className="stats-card-value">{(totalGroupConvertUnit)}</div>
                </div>

                <div className="stats-card">
                    <div className="stats-card-title">Total Net Price</div>
                    <div className="stats-card-value">{NumberFormatter.format(totalNetPrice)}</div>
                </div>

                <div className="stats-card">
                    <div className="stats-card-title">Total SPA Price</div>
                    <div className="stats-card-value">{NumberFormatter.format(totalSPAPrice)}</div>
                </div>

                <div className="stats-card">
                    <div className="stats-card-title">Total Group Net Price</div>
                    <div className="stats-card-value">{NumberFormatter.format(totalGroupNetPrice)}</div>
                </div>

                <div className="stats-card">
                    <div className="stats-card-title">Total Group SPA Price</div>
                    <div className="stats-card-value">{NumberFormatter.format(totalGroupSPAPrice)}</div>
                </div>
                
            </div>
            
            <div className="filter-row">
                
                <div className="filter-item">
                    <label>Date From</label>
                    <input
                        className="date-input"
                        type="date"
                        placeholder="YYYY-MM-DD"
                        onChange={(e) => {
                            e.persist();
                            setFilters((prev) => ({
                                ...prev,
                                dateFrom: e.target.value,
                            }));
                        }}
                        value={filters.dateFrom}
                    />
                </div>
                <div className="filter-item">
                    <label>Date To</label>
                    <input
                        className="date-input"
                        type="date"
                        placeholder="YYYY-MM-DD"
                        onChange={(e) => {
                            e.persist();
                            setFilters((prev) => ({
                                ...prev,
                                dateTo: e.target.value,
                            }));
                        }}
                        value={filters.dateTo}
                    />
                </div>
                <button
                    className="search-btn"
                    onClick={() => fetchDownlineSalesReportAgent()}
                >
                    Search
                </button>
                <button
                    className="reset-btn"
                    onClick={() => {
                        setFilters({
                            user: null,
                            dateTo: moment(Date.now()).format("YYYY-MM-DD"),
                            dateFrom: moment(Date.now()).subtract(1, "weeks").format("YYYY-MM-DD"),
                        });
                    }}
                >
                    Reset
                </button>
                <button 
                    className="search-btn"
                    onClick={() => {
                        let payload = [];
                        
                        if (downlineSalesReportData && downlineSalesReportData.fetchManualDownlineSalesReportAgent && downlineSalesReportData.fetchManualDownlineSalesReportAgent.data) {
                            downlineSalesReportData.fetchManualDownlineSalesReportAgent.data.forEach((item) => {
                                payload.push({
                                    Downline: item.user ? capitalizeWords(item.user) : "",
                                    // Personal Report Data
                                    PersonalUnitsSold: item.personalBookingSales || 0,
                                    PersonalSalesNetGDV: item.personalNetGdv || 0,
                                    PersonalConvertUnits: item.convertUnit || 0,
                                    PersonalConvertNetGDV: item.convertNetGdv || 0,
                                    PersonalConvertPercentage: item.convertPercentage || 0,
                                    PersonalNetGDVPercentage: item.netGdvPercentage || 0,
                                    PersonalTotalNetPrice: item.totalNetPrice || 0,
                                    PersonalTotalSPAPrice: item.totalSPAPrice || 0,
                                    // Group Report Data
                                    GroupUnitsSold: item.groupBookingSales || 0,
                                    GroupSalesNetGDV: item.groupPersonalNetGdv || 0,
                                    GroupConvertUnits: item.groupConvertUnit || 0,
                                    GroupConvertNetGDV: item.groupConvertNetGdv || 0,
                                    GroupConvertPercentage: item.groupConvertPercentage || 0,
                                    GroupNetGDVPercentage: item.groupNetGdvPercentage || 0,
                                    GroupNetPrice: item.groupNetPrice || 0,
                                    GroupSPAPrice: item.groupSPAPrice || 0,
                                });
                            });
                        }

                        excel({
                            excelData: payload,
                            fileName: `ManualDownlineReport-${filters.dateFrom}-${filters.dateTo}`,
                        });
                    }}
                >
                    Export
                </button>
            </div>

            <div className="report-tabs">
                <button
                    className={`report-tab ${report === "Personal Report" ? "active" : ""}`}
                    onClick={() => setReport("Personal Report")}
                >
                    Personal Report
                </button>
                <button
                    className={`report-tab ${report === "Group Report" ? "active" : ""}`}
                    onClick={() => setReport("Group Report")}
                >
                    Group Report
                </button>
            </div>

            <div className="report-table-wrapper">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 mb-4 col-gap-4">
                            <div className="mb-4">
                                <div className="flex p-8 w-full h-full px-8 rounded-lg block greyOpaqueBackground border borderorange font-semibold items-center">
                                <img className="h-full w-16" src="https://venti-fans.com/wp-content/uploads/2025/03/DD_SystemImages_Icon-01_0X6DfeObd.webp" alt="totaldownline" />
                                    <div className="ml-4 text-white text-sm">
                                        Total Downlines:{" "}
                                        <div className="text-sm lg:text-2xl">
                                            {totalDownlines}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-4">
                                <div className="flex p-8 w-full h-full px-8 rounded-lg block greyOpaqueBackground border borderorange font-semibold items-center">
                                <img className="h-full w-16" src="https://venti-fans.com/wp-content/uploads/2025/03/DD_SystemImages_Icon-01_0X6DfeObd.webp" alt="totalsales" />
                                    <div className="ml-4 text-white text-sm">
                                        Total Sales Units:
                                        <div className="text-sm lg:text-2xl">
                                            {totalSalesUnit}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="mb-4">
                                <div className="flex p-8 w-full h-full px-8 rounded-lg block greyOpaqueBackground border borderorange font-semibold items-center">
                                <img className="h-full w-16" src="https://ik.imagekit.io/dnddecpho/SSR/D&D_SystemImages_Icon-08_Ig1C9Ats4h.png?updatedAt=1719631002561" alt="totalconvert" />
                                    <div className="ml-4 text-white text-sm">
                                        Total Convert Units:
                                        <div className="text-sm lg:text-2xl">
                                            {totalConvertUnit}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="mb-4">
                                <div className="flex p-8 w-full h-full px-8 rounded-lg block greyOpaqueBackground border borderorange font-semibold items-center">
                                <img className="h-full w-16" src="https://venti-fans.com/wp-content/uploads/2025/03/DD_SystemImages_Icon-04_F6rcRq_oO.webp" alt="totalgroupsales"/>
                                    <div className="ml-4 text-sm">
                                         Total Group Sales Units:
                                        <div className="text-sm lg:text-2xl">
                                            {totalGroupSalesUnit}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="mb-4">
                                <div className="flex p-8 w-full h-full px-8 rounded-lg block greyOpaqueBackground border borderorange font-semibold items-center">
                                <img className="h-full w-16" src="https://venti-fans.com/wp-content/uploads/2025/03/DD_SystemImages_Icon-06_VoI03VJDv.webp" alt="totalgroupconvert"/>
                                    <div className="ml-4 text-sm">
                                         Total Group Convert Units:
                                        <div className="text-sm lg:text-2xl">
                                            {totalGroupConvertUnit}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="mb-4">
                                <div className="flex p-8 w-full h-full px-8 rounded-lg block greyOpaqueBackground border borderorange font-semibold items-center">
                                <img className="h-full w-16" src="https://venti-fans.com/wp-content/uploads/2025/03/DD_SystemImages_Icon-06_VoI03VJDv.webp" alt="totalgroupconvert"/>
                                    <div className="ml-4 text-sm">
                                         Total Net Price:
                                        <div className="text-sm lg:text-2xl">
                                            {totalNetPrice}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="mb-4">
                                <div className="flex p-8 w-full h-full px-8 rounded-lg block greyOpaqueBackground border borderorange font-semibold items-center">
                                <img className="h-full w-16" src="https://venti-fans.com/wp-content/uploads/2025/03/DD_SystemImages_Icon-06_VoI03VJDv.webp" alt="totalgroupconvert"/>
                                    <div className="ml-4 text-sm">
                                         Total SPA Price:
                                        <div className="text-sm lg:text-2xl">
                                            {totalSPAPrice}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="mb-4">
                                <div className="flex p-8 w-full h-full px-8 rounded-lg block greyOpaqueBackground border borderorange font-semibold items-center">
                                <img className="h-full w-16" src="https://venti-fans.com/wp-content/uploads/2025/03/DD_SystemImages_Icon-06_VoI03VJDv.webp" alt="totalgroupconvert"/>
                                    <div className="ml-4 text-sm">
                                         Total Group Net Price:
                                        <div className="text-sm lg:text-2xl">
                                            {totalGroupNetPrice}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="mb-4">
                                <div className="flex p-8 w-full h-full px-8 rounded-lg block greyOpaqueBackground border borderorange font-semibold items-center">
                                <img className="h-full w-16" src="https://venti-fans.com/wp-content/uploads/2025/03/DD_SystemImages_Icon-06_VoI03VJDv.webp" alt="totalgroupconvert"/>
                                    <div className="ml-4 text-sm">
                                    Total Group SPA Price:
                                        <div className="text-sm lg:text-2xl">
                                            {totalGroupSPAPrice}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                {report === "Personal Report" && (
                <div className="mb-16">
                    <div className="text-gray-900 font-bold py-4 text-2xl lg:text-3xl text-center bg-gradient capitalize">
                        Personal Report
                    </div>
                    <Table
                        columns={columns}
                        data={
                            downlineSalesReportData
                                ? downlineSalesReportData.fetchManualDownlineSalesReportAgent
                                    ? downlineSalesReportData
                                          .fetchManualDownlineSalesReportAgent.data
                                    : []
                                : []
                        }
                        updatePagination={setPaginationState}
                        paginationState={paginationState}
                        loading={downlineSalesReportLoading}
                    />
                </div>
                )}
                {report === "Group Report" && (
                    <Table
                        columns={groupColumns}
                        data={
                            downlineSalesReportData
                                ? downlineSalesReportData
                                    .fetchManualDownlineSalesReportAgent.data
                                : []
                        }
                        loading={downlineSalesReportLoading}
                    />
                )}
            </div>
        </div>
    );
};

export default withUser(DownlineReport);